import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FmAutocompleteInput, FmButton, FmCenteredTabs, FmMessage, FmTextField } from "../../Components";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { FmMulticoteAgriCard } from "../MulticoteAgriSolution/Components";
import { useIsMobile } from "../../Hooks";
import { useDispatch, useSelector } from "react-redux";
import { AgriSolutionState, AuthState, GeneralState } from "../../Redux";
import { updateUserProfileThunk } from "../../Redux/States/Auth/AuthThunk";
import { BlendStat, RawMaterial } from "../../Models";

import useStyles from "./UserAreaPage.style";
import { NITROGEN_MAX_LIMIT, NUTRIENTS_COUNT, NUTRIENTS_SUM_MAX_LIMIT, PHOSPHORUS_COUNT, POTASSIUM_MAX_LIMIT } from "../../Utils/Consts";

interface IFormInputs {
  country: string;
  region: string | undefined;
  fullName: string;
  email: string;
  mainInterest: string;
  expertise: string;
  unCoated: string | undefined;
}

const UserAreaPage = () => {
  const [toggleCountryUpdate, setToggleCountryUpdate] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const user = useSelector(GeneralState.Selectors.selectUser);
  const { countries, regionsByCountry, specializations, mainInterests } = useSelector(AuthState.Selectors.selectRegistrationData);
  const rememberUser = useSelector(GeneralState.Selectors.selectUserWantsToBeRemembered);
  const rawMaterialBank = useSelector(AgriSolutionState.Selectors.selectRawMaterialBank);

  const {
    handleSubmit,
    getValues,
    setValue,
    control,
    register,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: "onBlur",
    defaultValues: {
      fullName: user?.name,
      email: user?.email,
      country: user?.country,
      expertise: user?.expertise,
      region: user?.region,
      mainInterest: user?.mainInterest,
    },
  });

  useEffect(() => {
    (window as any).dataLayer.push({
      event: "UserAreaPage",
      pageUrl: "https://multimatch.haifa-group.com/multi-match/UserAreaPage",
      pageTitle: "UserAreaPage",
    });
  }, []);

  const handleTabSwitch = (newValue: number) => {
    setTabValue(newValue);
  };
  const handleFormSubmit: SubmitHandler<IFormInputs> = async (data) => {
    await dispatch(
      updateUserProfileThunk(
        {
          // role: user?.role as string,
          name: data.fullName,
          email: data.email,
          expertise: data.expertise,
          country: data.country,
          region: data.region,
          mainInterest: data.mainInterest,
        },
        rememberUser,
      ),
    );

    setShowSuccessMessage(true);
  };

  const regions = useMemo(() => {
    const country = getValues("country");
    const countriesRegions = regionsByCountry[country];
    return countriesRegions || [];
  }, [regionsByCountry, toggleCountryUpdate, getValues]);

  const closeMessageHandler = () => {
    setShowSuccessMessage(false);
  };

  const addItem = async (item: RawMaterial | any) => {
    try {
      if (item !== undefined && item !== null) {
        const isValid = isMaterialValid(item);
        if (isValid) {
          const formula = item.toString().split("-");
          await dispatch(
            AgriSolutionState.Thunks.addRawMaterials({
              N: Number(formula[0]),
              P2O5: Number(formula[1]),
              K2O: Number(formula[2]),
            }),
          );
          setValue("unCoated", undefined);
        } else {
          // errors.unCoated = 'error!!';
        }
      }
    } catch (e) {
      //
    }
  };

  const isMaterialValid = (item: any): boolean => {
    if (item === undefined || item == null) {
      return true;
    }
    if (item.length === 0) {
      return true;
    }
    try {
      if (item && item.length > 0) {
        const formula = item.toString().split("-");
        if (formula.length !== NUTRIENTS_COUNT) return false;
        else {
          const sum = Number(formula[0]) + Number(formula[1]) + Number(formula[2]);
          return (
            Number(formula[0]) <= NITROGEN_MAX_LIMIT &&
            Number(formula[1]) <= PHOSPHORUS_COUNT &&
            Number(formula[2]) <= POTASSIUM_MAX_LIMIT &&
            sum <= NUTRIENTS_SUM_MAX_LIMIT
          );
        }
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  };

  const removeItem = async (item: BlendStat) => {
    item && item.id && (await dispatch(AgriSolutionState.Thunks.deleteRawMaterialById(item.id)));
  };
  return (
    <>
      {tabValue === 0 ? (
        <form onSubmit={handleSubmit(handleFormSubmit)} className={isMobile ? classes.mobile_container : classes.container}>
          <div className={isMobile ? classes.mobile_title : classes.title}>{t("userAreaPage.title")}</div>
          <div className={isMobile ? classes.mobile_tabs_container : classes.tabs_container}>
            <FmCenteredTabs tabValue={tabValue} setTabValue={handleTabSwitch} tabsNames={[t("userAreaPage.tab1"), t("userAreaPage.tab2")]} />
          </div>
          <div className={isMobile ? classes.mobile_inputs_container : classes.inputs_container}>
            <div className={isMobile ? classes.mobile_inputs_firstRow : classes.inputs_firstRow}>
              <div className={classes.inputWrapper}>
                <Controller
                  name={"fullName"}
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("validationErrors.fieldIsRequired"),
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <FmTextField
                        {...register("fullName")}
                        {...field}
                        label={t("userAreaPage.inputLabel1")}
                        variant="filled"
                        type="text"
                        fullWidth="100%"
                        error={errors.fullName}
                        value={field.value}
                        helperText={errors.fullName?.message || ""}
                      />
                    );
                  }}
                />
              </div>
              <div className={classes.inputWrapper}>
                <Controller
                  name={"country"}
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("validationErrors.fieldIsRequired"),
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <FmAutocompleteInput
                        {...register("country")}
                        {...field}
                        label={t("userAreaPage.inputLabel2")}
                        error={errors.country}
                        noOptionsText={false}
                        helperText={errors.country?.message || ""}
                        data={countries}
                        getLabel={(country: any) => t(country.code ? `countries.${country.code}` : `countries.${country}`)}
                        setValue={(country: any) => {
                          setToggleCountryUpdate((update) => !update);
                          setValue("region", undefined);
                          setValue("country", country && country.code ? country.code : undefined);
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <div className={isMobile ? classes.mobile_inputs_secondRow : classes.inputs_secondRow}>
              <div className={classes.inputWrapper}>
                <Controller
                  name={"region"}
                  control={control}
                  key={regions && regions[0] && regions[0].countryCode}
                  render={({ field }) => {
                    return (
                      <FmAutocompleteInput
                        {...register("region")}
                        {...field}
                        label={t("contactUs.inputLabel2")}
                        error={errors.region}
                        helperText={errors.region?.message || ""}
                        data={regions}
                        getLabel={(region: any) => t(region && region.code ? `regions.${region.code}` : `regions.${region}`)}
                        setValue={(region: any) => {
                          setValue("region", region && region.code ? region.code : undefined);
                        }}
                      />
                    );
                  }}
                />
              </div>
              <div className={classes.inputWrapper}>
                <Controller
                  name={"email"}
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("validationErrors.fieldIsRequired"),
                    },
                  }}
                  render={({ field }) => {
                    return (
                      <FmTextField
                        {...register("email")}
                        {...field}
                        label={t("userAreaPage.inputLabel3")}
                        variant="filled"
                        type="text"
                        fullWidth="100%"
                        error={errors.email}
                        value={field.value}
                        helperText={errors.email?.message || ""}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <div className={isMobile ? classes.mobile_inputs_thirdRow : classes.inputs_thirdRow}>
              <div className={isMobile ? classes.mobile_inputs_secondRow : classes.inputs_secondRow}>
                <div className={classes.inputWrapper}>
                  <Controller
                    name={"mainInterest"}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("validationErrors.fieldIsRequired"),
                      },
                    }}
                    render={({ field }) => {
                      return (
                        <FmAutocompleteInput
                          {...register("mainInterest")}
                          {...field}
                          label={t("userAreaPage.inputLabel4")}
                          error={errors.mainInterest}
                          noOptionsText={true}
                          helperText={errors.mainInterest?.message || ""}
                          data={mainInterests}
                          getLabel={(main: any) => t(main.code ? `mainInterests.${main.code}` : `mainInterests.${main}`)}
                          setValue={(main: any) => {
                            setValue("mainInterest", main && main.code ? main.code : undefined);
                          }}
                        />
                      );
                    }}
                  />
                </div>
                <div className={classes.inputWrapper}>
                  <Controller
                    name={"expertise"}
                    control={control}
                    render={({ field }) => {
                      return (
                        <FmAutocompleteInput
                          {...register("expertise")}
                          {...field}
                          label={t("userAreaPage.inputLabel5")}
                          error={errors.expertise}
                          noOptionsText={true}
                          helperText={errors.expertise?.message || ""}
                          data={specializations}
                          getLabel={(experty: any) => t(experty.code ? `specialties.${experty.code}` : `specialties.${experty}`)}
                          setValue={(experty: any) => {
                            setValue("expertise", experty && experty.code ? experty.code : undefined, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        />
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={classes.button_container}>
            <FmButton fullWidth={isMobile ? "fullWidth" : "regular"} type="submit">
              {t("userAreaPage.button")}
            </FmButton>
          </div>
          {showSuccessMessage && (
            <FmMessage className={classes.message} variant={"success"} closeMessage={closeMessageHandler} snackbar={true}>
              {t("userAreaPage.messages.success")}
            </FmMessage>
          )}
        </form>
      ) : (
        <div className={isMobile ? classes.mobile_container : classes.container}>
          <div className={isMobile ? classes.mobile_title : classes.title}>{t("userAreaPage.title")}</div>
          <div className={isMobile ? classes.mobile_tabs_container : classes.tabs_container}>
            <FmCenteredTabs tabValue={tabValue} setTabValue={handleTabSwitch} tabsNames={[t("userAreaPage.tab1"), t("userAreaPage.tab2")]} />
          </div>
          <div className={classes.input_container}>
            <div className={classes.inputWrapper}>
              <Controller
                name={"unCoated"}
                control={control}
                render={({ field }) => {
                  return (
                    <FmAutocompleteInput
                      {...register("unCoated")}
                      {...field}
                      label={t("userAreaPage.inputSecondPage")}
                      error={errors.unCoated}
                      noOptionsText={true}
                      freeSolo
                      helperText={errors.unCoated?.message || <span className={classes.helperText}>{t("userAreaPage.uncoatedInputHelperText")}</span>}
                      data={rawMaterialBank ?? []}
                      getLabel={(item: any) => (item.name ? item.name : item)}
                      setValue={(item: any) => {
                        setValue("unCoated", item && item.name ? item.name : item);
                        if (item) addItem(item);
                      }}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className={isMobile ? classes.mobile_uncoatedArray_container : classes.uncoatedArray_container}>
            {rawMaterialBank &&
              rawMaterialBank.map((co: any) => (
                <FmMulticoteAgriCard key={co.name} name={co.name} percentage={co.precentage} isDisplayed={false} onRemove={() => removeItem(co)} />
              ))}
          </div>
        </div>
      )}
    </>
  );
};

export default UserAreaPage;
